// NOTE: 現状PCのみで使用。将来的にSPもこちらのsliceを使用する
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ParsedUrlQuery } from 'node:querystring';
import { validateParams } from '../../../logics/validation';
import {
  AppQueryParams,
  QueryParams,
  QUERY_PARAMS,
} from '../../../constants/queryParams';
import { decodeKeyword } from '../../../utils/decodeUtil';
import { RootState } from '../../store';
import { IGNORE_SCOUTLIST_ID } from '../../../constants/scoutServiceJob';

// 初期状態
export const queryParamsInitialState: {
  pureQueryParams: ParsedUrlQuery;
  queryParams: AppQueryParams;
} = {
  pureQueryParams: {},
  queryParams: {},
};

/**
 * State型
 */
export interface QueryParamsSliceState {
  pureQueryParams: ReturnType<typeof queryParamsReducer>;
  queryParams: ReturnType<typeof queryParamsReducer>;
}

/**
 * query Slice
 * @remarks クエリパラメーターをバリデーションして保存する
 */
export const queryParamsSlice = createSlice({
  name: 'queryParams',
  initialState: queryParamsInitialState,
  reducers: {
    setPureQueryParams: (state, action: PayloadAction<QueryParams>) => {
      state.pureQueryParams = { ...action.payload };
    },
    setQueryParams: (state, action: PayloadAction<QueryParams>) => {
      const requiredQueryList = Object.values(QUERY_PARAMS);
      const requiredQueryMap = deleteUnnecessaryPropaties(
        action.payload,
        requiredQueryList
      );
      const validatedQuery = validateParams(requiredQueryMap);
      const newCond = castToAppCondition(validatedQuery);
      state.queryParams = {
        ...state.queryParams,
        ...newCond,
      };
    },
  },
});

// Create Action
export const { setPureQueryParams, setQueryParams } = queryParamsSlice.actions;

// reducer
export const queryParamsReducer = queryParamsSlice.reducer;

// selector
// TODO: pureQueryParamsSelector は sp でだけ使っているけど、pc は store に app がないので pc では使えない。なのでこの selector を sp に移す
/**
 * 受け取ったQueryのまま保存する。
 * @param state
 * @returns クエリ
 */
export const pureQueryParamsSelector = (state): ParsedUrlQuery =>
  state.app.pureQueryParams;
/**
 * アプリで使用する形のまま返す。(例：st: [a,s,d])
 * @param state
 * @returns クエリ
 */
export const appQueryParamsSelector = (state): AppQueryParams =>
  state.queryParams.queryParams;

// URLのクエリパラメーター定式で返す。
// (例：st: [a,s,d] → st:'a,s,d' )
export const queryParamsSelector = createSelector(
  appQueryParamsSelector,
  (state) => {
    return castToQueryCondition(state || {});
  }
);

export const condQueries = [
  'oc',
  'ar',
  'pr',
  'ci',
  'wa',
  'rt',
  'st',
  'ind',
  'ha',
  'k',
  'ni',
  'ne',
  'es',
  'op',
  'tp',
  'pf',
  'ds',
  'fid',
  'qc',
];

export const condQuerySelector = createSelector(
  appQueryParamsSelector,
  (qp) => {
    return Object.keys(qp).reduce<AppQueryParams>((pre, cur) => {
      if (condQueries.includes(cur)) {
        return { ...pre, [cur]: qp[cur] };
      }
      return pre;
    }, {});
  }
);

/**
 * dsクエリのみを返す
 * @param state
 * @returns dsクエリ
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function dsQuerySelector(state: any): AppQueryParams['ds'] {
  return state.queryParams.queryParams.ds;
}

/**
 * ページ番号のみを返す
 * @param state
 * @returns ページ番号
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function pageQuerySelector(state: any): number {
  return Number(state.queryParams.queryParams.page) || 1;
}

/**
 * スカウトIDを取得するセレクタ
 * @param state
 */
export function scoutIdSelector(state: RootState) {
  const query = state.queryParams.queryParams;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if (query.wlstid) return query.wlstid;
  return (
    (query.scoutlist_id &&
      !IGNORE_SCOUTLIST_ID.includes(query.scoutlist_id as never) &&
      query.scoutlist_id) ||
    undefined
  );
}

/**
 * 求人IDを取得するセレクタ
 * @param state
 */
export function jidSelector(state: RootState) {
  const { jid = [] } = state.queryParams.queryParams;
  return jid[0] || '';
}

const deleteUnnecessaryPropaties = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  map: Record<string, any>,
  requiredProps: string[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Record<string, any> => {
  const newMap = {};
  requiredProps.forEach((prop) => {
    const value = map[prop];
    if (value !== undefined) {
      newMap[prop] = value;
    }
  });
  return newMap;
};

/**
 * クエリをQueryParams型に変換する
 * @param cond
 * @returns 返還後のクエリ
 */
export const castToQueryCondition = (cond: AppQueryParams): QueryParams => {
  const result = {};
  Object.keys(cond).forEach((key) => {
    const value: string | string[] = cond[key];
    if (Array.isArray(value)) {
      result[key] = value.join();
    } else if (key === QUERY_PARAMS.k) {
      // k（キーワード）をデコードする。
      result[key] = decodeKeyword(value);
    } else {
      result[key] = value;
    }
  });
  return result;
};

/**
 * クエリをAppQueryParams型に変換する
 * @param cond
 * @returns クエリ
 */
export const castToAppCondition = (cond: QueryParams): AppQueryParams => {
  const result = {};
  // 配列に変化する条件名
  const castAryCond = [
    QUERY_PARAMS.jid,
    QUERY_PARAMS.ar,
    QUERY_PARAMS.pr,
    QUERY_PARAMS.ci,
    QUERY_PARAMS.wa,
    QUERY_PARAMS.rt,
    QUERY_PARAMS.st,
    QUERY_PARAMS.oc,
    QUERY_PARAMS.ind,
    QUERY_PARAMS.op,
    QUERY_PARAMS.es,
    QUERY_PARAMS.ne,
    QUERY_PARAMS.ha,
  ];
  Object.keys(cond).forEach((key) => {
    const value: undefined | string = cond[key];
    // 空文字は値なし判定にする
    if (value) {
      // 特定の値はは配列にする。
      if (castAryCond.includes(key)) {
        result[key] = value.split(',');
      } else {
        result[key] = value;
      }
    }
  });
  return result;
};
